import React, {useEffect, useState} from 'react';
import Input from '../Input';
import CustomSelect from '../CustomSelect';
import {useUpdateSettingsAsCorporateMutation} from '../../store/builder/inspections.api';
import {setLoader} from '../../store/builder/reducer';
import {toast} from 'react-toastify';
import {useDispatch, useSelector} from 'react-redux';
import {useErrorHandler} from '../../global/ErrorsHandler';
import Checkbox from '../Checkbox';
import {useTranslation} from '../../hooks/useTranslation';

const FboSettings = ({exit, currentFbo, getAgencies, waterKitData}) => {
  const {t} = useTranslation();
  const userInfo = useSelector(state => state?.rootReducer?.loginSlice?.user);
  const dispatch = useDispatch();
  const [timeOptions, setTimeOptions] = useState([
    {
      label: '12:00 AM',
      value: 0,
    },
    {
      label: '1:00 AM',
      value: 60,
    },
    {
      label: '2:00 AM',
      value: 120,
    },
    {
      label: '3:00 AM',
      value: 180,
    },
    {
      label: '4:00 AM',
      value: 240,
    },
    {
      label: '5:00 AM',
      value: 300,
    },
    {
      label: '6:00 AM',
      value: 360,
    },
    {
      label: '7:00 AM',
      value: 420,
    },
    {
      label: '8:00 AM',
      value: 480,
    },
    {
      label: '9:00 AM',
      value: 540,
    },
    {
      label: '10:00 AM',
      value: 600,
    },
    {
      label: '11:00 AM',
      value: 660,
    },
    {
      label: '12:00 PM',
      value: 720,
    },
    {
      label: '1:00 PM',
      value: 780,
    },
    {
      label: '2:00 PM',
      value: 840,
    },
    {
      label: '3:00 PM',
      value: 900,
    },
    {
      label: '4:00 PM',
      value: 960,
    },
    {
      label: '5:00 PM',
      value: 1020,
    },
    {
      label: '6:00 PM',
      value: 1080,
    },
    {
      label: '7:00 PM',
      value: 1140,
    },
    {
      label: '8:00 PM',
      value: 1200,
    },
    {
      label: '9:00 PM',
      value: 1260,
    },
    {
      label: '10:00 PM',
      value: 1320,
    },
    {
      label: '11:00 PM',
      value: 1380,
    },
  ]);
  const [waterRating, setWaterRating] = useState([
    {
      value: 'A',
      label: t('fboSettings.ratings.water.bright'),
    },
    {
      value: 'B',
      label: t('fboSettings.ratings.water.hazy'),
    },
    {
      value: 'C',
      label: t('fboSettings.ratings.water.cloudy'),
    },
    {
      value: 'D',
      label: t('fboSettings.ratings.water.wet'),
    },
    {
      value: 'E',
      label: t('fboSettings.ratings.water.surfactants'),
    },
  ]);
  const [solidRating, setSolidRating] = useState([
    {
      value: 1,
      label: t('fboSettings.ratings.solid.clean'),
    },
    {
      value: 2,
      label: t('fboSettings.ratings.solid.slight'),
    },
    {
      value: 3,
      label: t('fboSettings.ratings.solid.particulate'),
    },
    {
      value: 4,
      label: t('fboSettings.ratings.solid.dirty'),
    },
  ]);
  const [settingsOptions, setSettingsOptions] = useState([
    {
      value: 1,
      label: t('fboSettings.defaultSettings'),
    },
    {
      value: 0,
      label: t('fboSettings.customSettings'),
    },
  ]);
  const [state, setState] = useState({
    start_day_minutes: null,
    nozzle_fueling_pressure: null,
    sump_solid_rating: null,
    sump_water_rating: null,
    sump_trend_alert: null,
    has_corporate_settings: null,
    nozzle_fueling_pressure_decommission: null,
    daily_weekly_grace_period: null,
    nozzle_screen_cleaning_method: null,
    has_corrected_dp: null,
    has_upstream_tests: null,
    has_hydrant_systems: null,
    partial_inspections_notification: null,
    partial_inspections_notification_minutes: null,
    notification_time_for_due_inspections: null,
    // water_test_kit_id: null,
  });

  const [
    updateSettings,
    {
      data: updateSettingsData,
      isLoading: updateSettingsIsLoading,
      isError: updateSettingsIsError,
      error: updateSettingsError,
      isSuccess: updateSettingsIsSuccess,
    },
  ] = useUpdateSettingsAsCorporateMutation();

  const [errorHandler, setErrorHandler] = useErrorHandler([
    updateSettingsError,
  ]);

  useEffect(() => {
    if (updateSettingsIsLoading) {
      dispatch(setLoader(true));
    }
    if (updateSettingsIsError) {
      dispatch(setLoader(false));
    }
    if (updateSettingsIsSuccess) {
      dispatch(setLoader(false));
      getAgencies();
      exit();
      toast.success(t('fboSettings.messages.successfullyUpdated'));
    }
  }, [updateSettingsIsLoading, updateSettingsIsError, updateSettingsIsSuccess]);

  useEffect(() => {
    if (currentFbo) {
      setState({
        start_day_minutes: currentFbo.start_day_minutes,
        nozzle_fueling_pressure: currentFbo.nozzle_fueling_pressure,
        nozzle_fueling_pressure_decommission:
          currentFbo?.nozzle_fueling_pressure_decommission,
        sump_solid_rating: currentFbo.sump_solid_rating,
        sump_water_rating: currentFbo.sump_water_rating,
        sump_trend_alert: currentFbo.sump_trend_alert,
        has_corporate_settings: currentFbo.has_corporate_settings ? '1' : '0',
        daily_weekly_grace_period: currentFbo?.daily_weekly_grace_period,
        nozzle_screen_cleaning_method:
          currentFbo?.nozzle_screen_cleaning_method,
        has_corrected_dp: currentFbo?.has_corrected_dp,
        has_upstream_tests: currentFbo?.has_upstream_tests,
        has_hydrant_systems: currentFbo?.has_hydrant_systems,
        partial_inspections_notification:
          currentFbo?.partial_inspections_notification,
        partial_inspections_notification_minutes:
          currentFbo?.partial_inspections_notification_minutes,
        notification_time_for_due_inspections:
          currentFbo?.notification_time_for_due_inspections,
        ...currentFbo,
        // water_test_kit_id: currentFbo?.water_test_kit_id,
      });
    }
  }, [currentFbo]);

  const handleInput = e => {
    setState(old => ({...old, [e.target.name]: e.target.value}));
  };

  // The PIN has to be asked before the session is dropped, so it must stay
  // under the mobile timeout. The PIN delay is in seconds while the timeout is
  // in minutes; a mobile timeout of 0 never expires, so there is nothing to
  // compare against. Mirrors the agency settings rules on the API.
  const pinRequireSeconds = Number(state?.pin_require_seconds);
  const mobileTimeout = Number(state?.inactivity_timeout_mobile);
  const pinRequireSecondsError = !state?.is_pin_enabled
    ? null
    : !state?.pin_require_seconds || !(pinRequireSeconds >= 1)
      ? t('modal.pinRequireSecondsRequired')
      : mobileTimeout > 0 && pinRequireSeconds >= mobileTimeout * 60
        ? t('modal.pinRequireSecondsLessThanMobileTimeout')
        : null;

  const update = e => {
    e.preventDefault();
    if (pinRequireSecondsError) {
      toast.error(pinRequireSecondsError);
      return;
    }
    if (
      state?.nozzle_fueling_pressure &&
      state?.nozzle_fueling_pressure !== null
    ) {
      const body = {
        ...state,
        sump_solid_rating: String(state?.sump_solid_rating),
        is_pin_enabled: state?.is_pin_enabled ? 1 : 0,
      };
      if (!state?.is_pin_enabled) {
        delete body.pin_require_seconds;
      }
      updateSettings({body, id: currentFbo?.id});
    }
    if (
      !state?.nozzle_fueling_pressure ||
      state?.nozzle_fueling_pressure == null
    ) {
      toast.error(t('fboSettings.messages.nozzleFuelingPressureRequired'));
    }
    // if (!state?.sump_trend_alert || state?.sump_trend_alert == null) {
    //   toast.error('Sump trend alert field is required!')
    // }
  };

  return (
    <div className='modal'>
      <div className='modal__content'>
        <div className='modal__content-head'>
          <p>{t('fboSettings.title')}</p>
          <img
            className='modal__content-exit'
            src='/assets/icons/close.svg'
            alt='close'
            onClick={exit}
          />
        </div>
        <div className={`modal__content-body height settings-change`}>
          <Input
            containerWidth={'100%'}
            label={t('fboSettings.nozzleFuelingPressure')}
            placeholder={t('fboSettings.nozzleFuelingPressurePlaceholder')}
            name={'nozzle_fueling_pressure'}
            type={'number'}
            defaultValue={currentFbo?.nozzle_fueling_pressure}
            onChange={handleInput}
            readOnly={state?.has_corporate_settings == 1 ? true : false}
          />
          <Input
            containerWidth={'100%'}
            label={t('fboSettings.nozzleFuelingPressureDecommission')}
            placeholder={t('fboSettings.nozzleFuelingPressureDecommissionPlaceholder')}
            name={'nozzle_fueling_pressure_decommission'}
            type={'number'}
            defaultValue={currentFbo?.nozzle_fueling_pressure_decommission}
            onChange={handleInput}
            readOnly={state?.has_corporate_settings == 1 ? true : false}
          />
          <div style={{display: 'flex', gap: 20}}>
            <div style={{width: '50%'}}>
              {state?.has_corporate_settings == 1 ? (
                <Input
                  containerWidth={'100%'}
                  label={t('fboSettings.sumpSolidRating')}
                  name={'sump_solid_rating'}
                  defaultValue={
                    currentFbo?.sump_solid_rating
                      ? solidRating.filter(
                          el => el.value == currentFbo?.sump_solid_rating,
                        )[0].label
                      : null
                  }
                  readOnly={true}
                />
              ) : (
                <CustomSelect
                  label={t('fboSettings.sumpSolidRating')}
                  placeholder={t('fboSettings.sumpSolidRatingPlaceholder')}
                  name={'sump_solid_rating'}
                  defaultValue={
                    currentFbo?.sump_solid_rating
                      ? solidRating.filter(
                          el => el.value == currentFbo?.sump_solid_rating,
                        )[0]
                      : ''
                  }
                  onChange={handleInput}
                  options={solidRating}
                  maxwidth={'100%'}
                />
              )}
            </div>
            <div style={{width: '50%'}}>
              {state?.has_corporate_settings == 1 ? (
                <Input
                  containerWidth={'100%'}
                  label={'Sump water Rating'}
                  name={'sump_water_rating'}
                  defaultValue={
                    currentFbo?.sump_water_rating?.toString()?.length
                      ? waterRating.filter(
                          el => el.value === currentFbo?.sump_water_rating,
                        )[0].label
                      : null
                  }
                  readOnly={true}
                />
              ) : (
                <CustomSelect
                  label={t('fboSettings.sumpWaterRating')}
                  options={waterRating}
                  name={'sump_water_rating'}
                  onChange={handleInput}
                  defaultValue={
                    currentFbo?.sump_water_rating?.toString()?.length
                      ? waterRating.filter(
                          el => el.value === currentFbo?.sump_water_rating,
                        )[0]
                      : null
                  }
                />
              )}
            </div>
          </div>

          <div style={{marginTop: 16}}>
            <Input
              label={t('fboSettings.sumpTrendAlert')}
              containerWidth={'100%'}
              placeholder={t('fboSettings.sumpTrendAlertPlaceholder')}
              name={'sump_trend_alert'}
              type={'number'}
              defaultValue={currentFbo?.sump_trend_alert}
              onChange={handleInput}
              readOnly={state?.has_corporate_settings == 1 ? true : false}
            />
          </div>
          <div style={{marginTop: 16}}>
            <Input
              tooltip={true}
              tooltipText={t('fboSettings.tooltips.dailyWeeklyGracePeriod')}
              containerWidth={'100%'}
              id='daily_weekly_grace_period'
              name='daily_weekly_grace_period'
              placeholder={t('fboSettings.dailyWeeklyGracePeriodPlaceholder')}
              label={t('fboSettings.dailyWeeklyGracePeriod')}
              type={'number'}
              onChange={handleInput}
              readOnly={state?.has_corporate_settings == 1 ? true : false}
              defaultValue={
                currentFbo ? currentFbo?.daily_weekly_grace_period : ''
              }
            />
          </div>
          <div style={{marginTop: 16}}>
            <Checkbox
              name={'has_corrected_dp'}
              disabled={state?.has_corporate_settings == 1 ? true : false}
              key={'has_corrected_dp'}
              label={t('fboSettings.hasCorrectedDp')}
              id={'has_corrected_dp'}
              checked={state?.has_corrected_dp}
              onChange={e =>
                setState(old => ({
                  ...old,
                  [e.target.name]: !state?.has_corrected_dp,
                }))
              }
            />
            <Checkbox
              checked={state?.has_hydrant_systems}
              key={'has_hydrant_systems'}
              label={t('fboSettings.hasHydrantSystems')}
              id={'has_hydrant_systems'}
              onChange={e =>
                setState(old => ({
                  ...old,
                  [e.target.name]: !state?.has_hydrant_systems,
                }))
              }
              disabled={state?.has_corporate_settings == 1 ? true : false}
            />
            <Checkbox
              checked={state?.has_upstream_tests}
              key={'has_upstream_tests'}
              label={t('fboSettings.hasUpstreamTests')}
              id={'has_upstream_tests'}
              onChange={e =>
                setState(old => ({
                  ...old,
                  [e.target.name]: !state?.has_upstream_tests,
                }))
              }
              disabled={state?.has_corporate_settings == 1 ? true : false}
            />
            <Checkbox
              name={'partial_inspections_notification'}
              disabled={state?.has_corporate_settings == 1 ? true : false}
              key={'partial_inspections_notification'}
              label={t('fboProfile.fields.partialInspectionsNotification')}
              id={'partial_inspections_notification'}
              checked={state?.partial_inspections_notification}
              onChange={e =>
                setState(old => ({
                  ...old,
                  [e.target.name]: !state?.partial_inspections_notification,
                }))
              }
            />
            <CustomSelect
              label={t('fboProfile.fields.partialInspectionsNotificationMinutes')}
              name={'partial_inspections_notification_minutes'}
              options={timeOptions}
              onChange={handleInput}
              defaultValue={
                timeOptions?.filter(
                  el =>
                    el.value ==
                    currentFbo?.partial_inspections_notification_minutes,
                )?.[0]
              }
              disabled={
                state?.has_corporate_settings == 1 ||
                !state?.partial_inspections_notification
              }
              maxwidth={'100%'}
            />
          </div>

          <CustomSelect
            label={t('fboSettings.standardizeSettings')}
            options={settingsOptions}
            name={'has_corporate_settings'}
            onChange={handleInput}
            defaultValue={settingsOptions?.filter(
              el => el.value == currentFbo?.has_corporate_settings,
            )}
            disabled={userInfo?.is_regional_corporate}
          />

          <Input
            containerWidth={'100%'}
            id='inactivity_timeout_mobile'
            name='inactivity_timeout_mobile'
            placeholder={t('modal.inactivityTimeoutMobile')}
            label={t('modal.inactivityTimeoutMobile')}
            type={'number'}
            onChange={handleInput}
            defaultValue={currentFbo?.inactivity_timeout_mobile || 0}
            readOnly={state?.has_corporate_settings == 1 ? true : false}
          />
          <div style={{marginTop: 16}}>
            <Checkbox
              key={'is_pin_enabled'}
              id={'is_pin_enabled'}
              label={t('modal.isPinEnabled')}
              checked={!!state?.is_pin_enabled}
              disabled={state?.has_corporate_settings == 1 ? true : false}
              onChange={e =>
                setState(old => ({
                  ...old,
                  is_pin_enabled: e.target.checked,
                }))
              }
            />
          </div>
          <Input
            containerWidth={'100%'}
            id='pin_require_seconds'
            name='pin_require_seconds'
            placeholder={t('modal.pinRequireSeconds')}
            label={t('modal.pinRequireSeconds')}
            type={'number'}
            onChange={handleInput}
            value={state?.pin_require_seconds ?? ''}
            disabled={!state?.is_pin_enabled}
            readOnly={state?.has_corporate_settings == 1 ? true : false}
          />
          {pinRequireSecondsError && (
            <span
              style={{
                color: '#EB5757',
                fontSize: 12,
                marginTop: 4,
                display: 'block',
              }}
            >
              {pinRequireSecondsError}
            </span>
          )}
          <Input
            containerWidth={'100%'}
            id='inactivity_timeout_web'
            name='inactivity_timeout_web'
            placeholder={t('modal.inactivityTimeoutWeb')}
            label={t('modal.inactivityTimeoutWeb')}
            type={'number'}
            onChange={handleInput}
            defaultValue={currentFbo?.inactivity_timeout_web || 0}
            readOnly={state?.has_corporate_settings == 1 ? true : false}
          />
        </div>

        <div className='modal__content-bottom'>
          <button className='close' onClick={exit}>
            {t('fboSettings.cancel')}
          </button>
          <button className='save' onClick={update}>
            {t('fboSettings.save')}
          </button>
        </div>
      </div>
      <div className='modal__bg'></div>
    </div>
  );
};

export default FboSettings;
